import { useMutation } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { toast } from 'sonner';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import type { Recital, RecitalSettings } from '../model/recital';

export interface UpdateRecitalParams {
  recitalId: string;
  name?: string;
  year?: number;
  startDate?: string;
  endDate?: string;
  description?: string;
  status?: 'draft' | 'published' | 'archived';
  settings?: Partial<RecitalSettings>;
}

export const updateRecitalAPI = async ({
  recitalId,
  ...params
}: UpdateRecitalParams) => {
  const response = await baseAPI.patch<Recital>(
    `/recitals/${recitalId}`,
    params,
  );
  return response.data;
};

export const useUpdateRecitalMutation = () => {
  return useMutation({
    mutationFn: async (params: UpdateRecitalParams) => {
      return await updateRecitalAPI(params);
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [KEYS.RECITALS],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.RECITALS, variables.recitalId],
      });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      const errorMessage =
        error.response?.data.message ?? 'Failed to update recital';
      toast.error(errorMessage);
    },
  });
};
